/**
 * PostHog — embudo Comunidad AR + admin `/admin/comunidad`.
 *
 * Filtro HogQL: siempre `site = 'delfuocampo'` (super-prop + payload).
 *
 * Eventos admin:
 *   - admin_comunidad_page_view
 *   - admin_comunidad_config_saved
 *   - admin_comunidad_config_save_failed
 *
 * Eventos visitante:
 *   - comunidad_landing_view  (wrapper canónico /invitacion-premium)
 *   - premium_invite_page_view (existente, reutilizado)
 *   - comunidad_reels_vip_click
 *   - dual_cta_* (vía DualCTAVideoLanding con product_surface=premium_invite_landing)
 */

import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import {
  getPostHogSlackSessionPropertiesPostHog,
  isPostHogConfigured,
  trackPremiumInvitePageViewPostHog,
} from "@/lib/posthogAnalytics";
import { getWorkerGeoAnalyticsProps } from "@/lib/serverTemplateDetection";

const SITE = BRAND_SOURCE_KEY || "delfuocampo";
const FUNNEL = "comunidad_ar";

function capture(event: string, props: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  try {
    const ph = (
      window as unknown as {
        posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void };
      }
    ).posthog;
    if (!ph?.capture) return;
    const geo = getWorkerGeoAnalyticsProps();
    const geoFlat = Object.fromEntries(
      Object.entries(geo).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    );
    ph.capture(event, {
      site: SITE,
      source: SITE,
      funnel: FUNNEL,
      product_surface: "comunidad",
      page_path: window.location.pathname,
      $current_url: window.location.href,
      timestamp: new Date().toISOString(),
      ...geoFlat,
      ...getPostHogSlackSessionPropertiesPostHog(),
      ...props,
    });
  } catch {
    /* never break UX */
  }
}

// ── Admin ───────────────────────────────────────────────────────────────────

export type ComunidadAdminConfigSnapshot = {
  simple_mux_overlay_enabled: boolean;
  simple_mux_overlay_playback_id: string | null;
  reels_playback_source: string;
  reels_curated_count: number;
  vip_checkout_base_url: string | null;
  free_telegram_url: string | null;
  dual_cta_free_enabled: boolean;
};

export function trackAdminComunidadPageViewPostHog(
  cfg: ComunidadAdminConfigSnapshot & { updated_at?: string | null },
): void {
  capture("admin_comunidad_page_view", {
    admin_surface: "comunidad",
    mux_overlay_enabled: cfg.simple_mux_overlay_enabled,
    has_mux_overlay_playback_id: Boolean(cfg.simple_mux_overlay_playback_id),
    reels_playback_source: cfg.reels_playback_source,
    reels_curated_count: cfg.reels_curated_count,
    has_vip_checkout_override: Boolean(cfg.vip_checkout_base_url),
    has_free_telegram_url: Boolean(cfg.free_telegram_url),
    dual_cta_free_enabled: cfg.dual_cta_free_enabled,
    config_updated_at: cfg.updated_at ?? null,
  });
}

export function trackAdminComunidadConfigSavedPostHog(params: {
  before: ComunidadAdminConfigSnapshot;
  after: ComunidadAdminConfigSnapshot;
}): void {
  const { before, after } = params;
  const changed: string[] = [];
  if (before.simple_mux_overlay_enabled !== after.simple_mux_overlay_enabled) {
    changed.push("simple_mux_overlay_enabled");
  }
  if (before.simple_mux_overlay_playback_id !== after.simple_mux_overlay_playback_id) {
    changed.push("simple_mux_overlay_playback_id");
  }
  if (before.reels_playback_source !== after.reels_playback_source) {
    changed.push("reels_playback_source");
  }
  if (before.reels_curated_count !== after.reels_curated_count) {
    changed.push("reels_curated_playback_ids");
  }
  if (before.vip_checkout_base_url !== after.vip_checkout_base_url) {
    changed.push("vip_checkout_base_url");
  }
  if (before.free_telegram_url !== after.free_telegram_url) {
    changed.push("free_telegram_url");
  }
  if (before.dual_cta_free_enabled !== after.dual_cta_free_enabled) {
    changed.push("dual_cta_free_enabled");
  }

  capture("admin_comunidad_config_saved", {
    admin_surface: "comunidad",
    fields_changed: changed,
    fields_changed_count: changed.length,
    mux_overlay_enabled: after.simple_mux_overlay_enabled,
    has_mux_overlay_playback_id: Boolean(after.simple_mux_overlay_playback_id),
    reels_playback_source: after.reels_playback_source,
    reels_curated_count: after.reels_curated_count,
    has_vip_checkout_override: Boolean(after.vip_checkout_base_url),
    has_free_telegram_url: Boolean(after.free_telegram_url),
    dual_cta_free_enabled: after.dual_cta_free_enabled,
  });
}

export function trackAdminComunidadConfigSaveFailedPostHog(error: string): void {
  capture("admin_comunidad_config_save_failed", {
    admin_surface: "comunidad",
    error_message: error.slice(0, 300),
  });
}

// ── Visitante ───────────────────────────────────────────────────────────────

export function trackComunidadLandingViewPostHog(params: {
  country: string;
  language: string;
  layout: "premium_simple" | "reels_feed" | string;
  vip_checkout_gateway?: string;
  has_vip_checkout_override?: boolean;
  has_free_telegram_url?: boolean;
  mux_overlay_enabled?: boolean;
  has_mux_overlay_playback_id?: boolean;
}): void {
  const shopRegion =
    String(params.country ?? "")
      .trim()
      .toUpperCase() === "AR"
      ? "AR"
      : "intl";

  // Evento canónico Comunidad + el histórico premium_invite_page_view (dashboards).
  capture("comunidad_landing_view", {
    shop_region: shopRegion,
    country: params.country,
    language: params.language,
    invite_layout: params.layout,
    vip_checkout_gateway: params.vip_checkout_gateway ?? null,
    has_vip_checkout_override: params.has_vip_checkout_override ?? false,
    has_free_telegram_url: params.has_free_telegram_url ?? false,
    mux_overlay_enabled: params.mux_overlay_enabled ?? null,
    has_mux_overlay_playback_id: params.has_mux_overlay_playback_id ?? false,
    landing_path: "/invitacion-premium",
    invite_surface: "dedicated_route",
  });

  if (isPostHogConfigured()) {
    trackPremiumInvitePageViewPostHog({
      country: params.country,
      language: params.language,
      has_free_telegram_url: params.has_free_telegram_url,
    });
  }
}

export function trackComunidadReelsVipClickPostHog(params: {
  country: string;
  vip_checkout_gateway: string;
  has_vip_checkout_override: boolean;
  cta_destination?: string;
}): void {
  const shopRegion =
    String(params.country ?? "")
      .trim()
      .toUpperCase() === "AR"
      ? "AR"
      : "intl";

  capture("comunidad_reels_vip_click", {
    shop_region: shopRegion,
    country: params.country,
    vip_checkout_gateway: params.vip_checkout_gateway,
    has_vip_checkout_override: params.has_vip_checkout_override,
    cta_destination: params.cta_destination ?? null,
    invite_layout: "reels_feed",
    landing_path: "/invitacion-premium",
  });
}

/** Props extra para dual_cta_* cuando product_surface = premium_invite_landing. */
export function comunidadDualCtaExtraProps(params: {
  country: string;
  vipCheckoutGateway: string;
  vipCheckoutOverrideActive: boolean;
  freeCtaEnabled: boolean;
}): Record<string, string | number | boolean | null> {
  const shopRegion =
    String(params.country ?? "")
      .trim()
      .toUpperCase() === "AR"
      ? "AR"
      : "intl";
  return {
    funnel: FUNNEL,
    shop_region: shopRegion,
    vip_checkout_gateway: params.vipCheckoutGateway,
    vip_checkout_override_active: params.vipCheckoutOverrideActive,
    dual_cta_free_enabled: params.freeCtaEnabled,
    landing_path: "/invitacion-premium",
  };
}
